import { useContext, useState } from 'react';
import type { ReactNode } from 'react';
import Modal from 'react-bootstrap/Modal';
import ButtonToolbar from 'react-bootstrap/ButtonToolbar';

import { Form, FormContext, ResetButton, SubmitButton } from '../forms';
import Button from './Button';
import type { ButtonProps } from './utils';

interface PromptButtonModalProps {
  show?: boolean;
  header?: ReactNode;
  /** Размер модалки react-bootstrap ('sm' | 'lg' | 'xl'). */
  size?: 'sm' | 'lg' | 'xl';
  cancelButtonTitle?: string;
  submitButtonTitle?: string;
  children?: ReactNode | ((formContext: any) => ReactNode);
  onClose?: (...args: unknown[]) => unknown;
}

const PromptButtonModal = (props: PromptButtonModalProps) => {
  // FormContext (forms/utils.ts): типизирован как FormContextType|undefined,
  // приводим к ожидаемой форме (Provider подаёт Formik-контекст).
  const formContext = useContext(FormContext) as {
    isSubmitting?: boolean;
    dirty?: boolean;
    [key: string]: any;
  };
  const {
    show,
    header,
    size,
    cancelButtonTitle,
    submitButtonTitle,
    children,
    onClose,
  } = props;

  return (
    <Modal
      show={ show }
      backdrop='static'
      size={ size }
      aria-labelledby='prompt-modal'
      onHide={ onClose }
    >
      <Modal.Header
        style={ { borderBottom: '1px solid #ececec', margin: '0 2px' } }
        closeButton
      >
        <Modal.Title id='prompt-modal'>
          { header }
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        { (typeof children === 'function') ? children(formContext) : children }
      </Modal.Body>

      <Modal.Footer style={ { borderTop: '1px solid #ececec', margin: '0 2px' } }>
        <ButtonToolbar className='float-end'>
          <ResetButton
            text={ cancelButtonTitle }
            disabled={ formContext.isSubmitting }
          />

          <SubmitButton
            text={ submitButtonTitle }
            disabled={ !formContext.dirty || formContext.isSubmitting }
          />
        </ButtonToolbar>
      </Modal.Footer>
    </Modal>
  );
};

/**
 * Пропсы кнопки с модалкой подтверждения: базовые пропсы Button, кроме
 * `children`/`onClick` (управляются кнопкой) и `size` (это размер модалки,
 * а не кнопки) + настройка модалки.
 */
export interface PromptButtonProps extends Omit<ButtonProps, 'children' | 'onClick' | 'size'> {
  /** Заголовок модалки. */
  header?: ReactNode;
  /** Размер модалки ('sm' | 'lg' | 'xl'); без пропа — стандартная ширина BS. */
  size?: 'sm' | 'lg' | 'xl';
  cancelButtonTitle?: string;
  submitButtonTitle?: string;
  onCancel?: (...args: unknown[]) => unknown;
  onClose?: (...args: unknown[]) => unknown;
  /** onSubmit формы модалки. */
  onSubmit?: (...args: unknown[]) => unknown;
  /** Контент модалки; функция получает formContext. */
  children?: ReactNode | ((formContext: any) => ReactNode);
}

const PromptButton = (props: PromptButtonProps) => {
  const [show, setShow] = useState(false);
  const {
    header,
    size,
    cancelButtonTitle = 'Отмена',
    submitButtonTitle = 'Подтвердить',
    children,
    onCancel,
    onClose,
    onSubmit,
    ...buttonProps
  } = props;

  const handleClose = () => {
    setShow(false);

    if (typeof onClose === 'function') {
      onClose();
    }
  };

  const handleReset = () => {
    setShow(false);

    if (typeof onCancel === 'function') {
      onCancel();
    }
  };

  return (
    <Form
      initialValues={ {} }
      onReset={ handleReset }
      onSubmit={ onSubmit as any }
      enableReinitialize
    >
      { show && (
        <PromptButtonModal
          show={ show }
          header={ header }
          size={ size }
          cancelButtonTitle={ cancelButtonTitle }
          submitButtonTitle={ submitButtonTitle }
          onClose={ handleClose }
        >
          { children }
        </PromptButtonModal>
      ) }

      <Button { ...buttonProps } onClick={ () => setShow(true) } />
    </Form>
  );
};

export default PromptButton;
